import { findIndex, isNull, isUndefined, kebabCase, size } from 'lodash-es'
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Divider,
  Grid,
  Input,
  Modal,
  Row,
  Space,
  Steps,
  Popconfirm,
} from 'antd'
import WarningOutlined from '@ant-design/icons/WarningOutlined'

import dayjs from 'dayjs'
import { useMemo, useRef, useState } from 'react'
import { Project } from 'types'

import {
  PlanningType,
  PlanningTypeModal,
} from '@components/projects/PlanningTypeModal'
import { useQuery } from 'urql'
import { graphql } from 'gql.tada'
import { usePermissions } from '@/hooks/auth/usePermissions'
import { useQueryClient } from '@tanstack/react-query'
import { GQL_CLIENT } from 'src/graphqlClient'
import { projectAction } from 'src/services/gql/projects'
import { AccountType, PlanningFileType, ProjectStep } from 'src/utils/enums'
import { PlanningFileTypeName } from 'src/utils/config'
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom'
import { CairoPlayTitle } from '@components/common/CairoPlayTitle'
import { queryKeys } from 'src/queryKeys'
import { synthesisStepPath } from 'src/routes/projects/edit/[id]/synthesis/synthesisSteps'
import { useProjectMenuOutletContext } from 'src/layouts/ProjectMenu'
import { ASSISTANT_TASKS_QUERY_KEY } from '@features/assistant/hooks/useAssistantTasks'
import { DepositPlatformPanel } from '@features/deposit-platforms/components/DepositPlatformPanel'
import {
  ChangeSalesmanControl,
  mayChangeSalesman,
} from '@features/salesmens/components/ChangeSalesmanControl'
import { isProjectHubRole } from 'src/utils/projectHubRole'
import { SalesmanSynthesisLayout } from 'src/layouts/SalesmanSynthesisLayout'

type ProjectDateField = keyof Pick<Project, 'created' | 'requested' | 'validated' | 'ordered' | 'paid' | 'started_at' | 'file_completed_at' | 'first_sending_date' | 'first_deposite_date' | 'instruction_end' | 'closed'>

const DATE_LABELS: Record<ProjectDateField, string> = {
  created: 'Créé le',
  requested: 'Demandé le',
  validated: 'Validé le',
  ordered: 'Commandé le',
  paid: 'Payé le',
  started_at: 'Démarré le',
  file_completed_at: 'Complété le',
  first_sending_date: 'Envoyé le',
  first_deposite_date: 'Déposé le',
  instruction_end: "Fin d'instruction",
  closed: 'Clôturé le',
}

type ProjectStepProgress = {
  keys: ProjectStep[]
  title: string
  dateFields?: ProjectDateField[]
  // icon?: (data: Project) => ReactNode
  // hide?: (data: Project) => boolean
}

export const steps: ProjectStepProgress[] = [
  {
    keys: [ProjectStep.PROJECT_INITIALIZED],
    title: 'Demande de projet',
    dateFields: ['created', 'requested'],
  },
  {
    keys: [ProjectStep.PROJECT_REQUESTED, ProjectStep.PROJECT_REFUSED],
    title: 'Contrôle des documents',
    dateFields: ['validated'],
    // hide: (data) => isUndefined(_.get(data, 'salesman.id')),
  },
  {
    keys: [
      ProjectStep.PROJECT_ACCEPTED,
      ProjectStep.PROJECT_ORDERED,
      ProjectStep.PROJECT_PAIED,
    ],
    title: 'Validation DP/PC & Paiement',
    dateFields: ['ordered', 'paid'],
    // hide: (data) => isUndefined(_.get(data, 'salesman.id')),
  },
  {
    keys: [ProjectStep.PROJECT_IN_PROGRESS, ProjectStep.PROJECT_FILE_COMPLETED],
    title: 'Rédaction des documents',
    dateFields: ['started_at', 'file_completed_at'],
  },
  {
    keys: [ProjectStep.PROJECT_FILE_SENT],
    title: 'Envoi des documents',
    dateFields: ['first_sending_date'],
  },
  {
    keys: [
      ProjectStep.PROJECT_FILE_SUBMITTED,
      ProjectStep.PROJECT_FILE_REFUSED,
    ],
    title: 'Traitement en mairie',
    dateFields: ['first_deposite_date', 'instruction_end'],
  },
  {
    keys: [ProjectStep.PROJECT_ABANDONED, ProjectStep.PROJECT_COMPLETED],
    title: 'Fin',
    dateFields: ['closed'],
  },
]

// Steps during which the commune's deposit platform is shown in the sidebar:
// "Rédaction des documents" (in progress / file completed), "Envoi des
// documents" (file sent) and "Traitement en mairie" (file submitted — the
// portal is where the file is lodged/tracked). See feature #542.
const DEPOSIT_PLATFORM_STEPS: readonly ProjectStep[] = [
  ProjectStep.PROJECT_IN_PROGRESS,
  ProjectStep.PROJECT_FILE_COMPLETED,
  ProjectStep.PROJECT_FILE_SENT,
  ProjectStep.PROJECT_FILE_SUBMITTED,
]

export type SynthesisOutletContext = {
  project: Project | undefined
  /**
   * Force-refresh the synthesis view: re-executes the layout's urql query
   * (network-only) and invalidates the React Query project caches. Step pages
   * must call this after a `project_action` so the step actually advances —
   * React Query invalidation alone never refetches the urql-driven step.
   */
  invalidateProject: () => void
}

export const useSynthesisOutletContext = (): SynthesisOutletContext =>
  useOutletContext<SynthesisOutletContext>()

const ProjectSynthesisLayoutDocument = graphql(`
  query ProjectSynthesisLayout($id: Int!) {
    projects_by_pk(id: $id) {
      id
      step
      project_type
      planning_file_type
      reserves
      created
      closed
      requested
      validated
      ordered
      paid
      started_at
      file_completed_at
      first_sending_date
      first_deposite_date
      instruction_end
      city
      plots(where: { main_plot: { _eq: true } }, limit: 1) {
        locality
        department_code
      }
      salesman {
        id
        first_name
        last_name
        phone
        account {
          email
        }
      }
      urban_analysis {
        status
        requestor
      }
    }
  }
`)

const ArchitectSynthesisLayout: React.FC = () => {
  const { data: accountType } = usePermissions()
  const queryClient = useQueryClient()
  const { id: routeId } = useParams<{ id: string }>()
  const projectID = Number(routeId)
  const navigate = useNavigate()
  const location = useLocation()
  const [{ data: queryData }, reexecute] = useQuery({
    query: ProjectSynthesisLayoutDocument,
    variables: { id: projectID },
    pause: !projectID,
  })
  const data = queryData?.projects_by_pk
    ? { data: queryData.projects_by_pk as unknown as Project }
    : undefined

  // Deposit platform (#542): resolve the project's commune from its main plot
  // and show the shared per-commune panel during the drafting/sending steps.
  const mainPlot = queryData?.projects_by_pk?.plots?.[0]
  const depositLocality = mainPlot?.locality ?? ''
  const depositDepartmentCode = mainPlot?.department_code ?? ''
  const depositCommune = queryData?.projects_by_pk?.city ?? ''
  const canEditDepositPlatform =
    accountType === AccountType.ARCHITECT ||
    accountType === AccountType.COLLABORATOR
  const showDepositPlatform =
    !!depositLocality &&
    DEPOSIT_PLATFORM_STEPS.includes(queryData?.projects_by_pk?.step as ProjectStep)

  const breakpoint = Grid.useBreakpoint()
  const isMobile = typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg
  const [showPlanningModal, setShowPlanningModal] = useState(false)
  const [showReservesModal, setShowReservesModal] = useState(false)
  const [reservesText, setReservesText] = useState<string>('')

  const menuCtx = useProjectMenuOutletContext()

  const invalidateProject = () => {
    reexecute({ requestPolicy: 'network-only' })
    // The outer ProjectMenu runs its own urql query (separate cache entry);
    // refetch it too so the nav menu reflects the mutation (e.g. the
    // "Étude urbaine" link appearing after an urban-analysis request).
    menuCtx?.refreshProjectMenu?.()
    queryClient.invalidateQueries({ queryKey: queryKeys.projects.all() })
    queryClient.invalidateQueries({ queryKey: ['project-status', projectID] })
    // Architect actions here can complete/create Floria assistant tasks
    // server-side; invalidate the task list so the panel reflects them
    // without a hard refresh.
    queryClient.invalidateQueries({ queryKey: ASSISTANT_TASKS_QUERY_KEY })
  }

  const [isUpdatingReserves, setIsUpdatingReserves] = useState(false)
  const [isAbandoning, setIsAbandoning] = useState(false)
  const [isDisabling, setIsDisabling] = useState(false)

  // Synchronous re-entrancy guard. State-based `disabled`/`loading` is set via
  // an async `setState`, so two fast clicks both pass before React re-renders.
  // A ref flips synchronously, so the second click is dropped immediately.
  const actionInFlight = useRef(false)

  const acceptProjectRequest = async (
    planningType: PlanningType,
    signing: boolean,
    abf: boolean,
    reserves: string | undefined
  ) => {
    if (actionInFlight.current) return
    actionInFlight.current = true
    try {
      const status = await projectAction(GQL_CLIENT, projectID as number, 'accept', {
        planningType: planningType,
        signing: signing,
        abf: abf,
        reserves: reserves,
      })
      setShowPlanningModal(false)
      invalidateProject()
      navigate(synthesisStepPath(projectID, status?.step), { replace: true })
    } finally {
      actionInFlight.current = false
    }
  }

  const updateReserves = async () => {
    if (actionInFlight.current) return
    actionInFlight.current = true
    setIsUpdatingReserves(true)
    try {
      const status = await projectAction(GQL_CLIENT, projectID as number, 'reserves', {
        reserves: reservesText,
      })
      setShowReservesModal(false)
      invalidateProject()
      navigate(synthesisStepPath(projectID, status?.step), { replace: true })
    } finally {
      actionInFlight.current = false
      setIsUpdatingReserves(false)
    }
  }

  const openReservesModal = () => {
    setReservesText(data?.data.reserves ?? '')
    setShowReservesModal(true)
  }
  const abandonProject = async () => {
    if (actionInFlight.current) return
    actionInFlight.current = true
    setIsAbandoning(true)
    try {
      const status = await projectAction(GQL_CLIENT, projectID as number, 'abandonment')
      invalidateProject()
      navigate(synthesisStepPath(projectID, status?.step), { replace: true })
    } finally {
      actionInFlight.current = false
      setIsAbandoning(false)
    }
  }
  const disableProject = async () => {
    if (actionInFlight.current) return
    actionInFlight.current = true
    setIsDisabling(true)
    try {
      await projectAction(GQL_CLIENT, projectID as number, 'disable')
      navigate('/projects', { replace: true })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all() })
    } finally {
      actionInFlight.current = false
      setIsDisabling(false)
    }
  }

  const currentItemIdx = useMemo(
    () =>
      findIndex(steps, (stepItem) => {
        const step = data?.data.step as ProjectStep
        if (!isUndefined(step)) {
          return stepItem.keys.includes(step)
        }
        return false
      }),
    [data?.data.step]
  )

  const currentItemStatus: 'process' | 'wait' | 'finish' | 'error' =
    useMemo(() => {
      switch (data?.data.step) {
        case ProjectStep.PROJECT_INITIALIZED:
        case ProjectStep.PROJECT_REQUESTED:
        case ProjectStep.PROJECT_ORDERED:
        case ProjectStep.PROJECT_IN_PROGRESS:
        case ProjectStep.PROJECT_FILE_SENT:
        case ProjectStep.PROJECT_FILE_SUBMITTED:
        case ProjectStep.PROJECT_ACCEPTED:
          return 'process'
        case ProjectStep.PROJECT_PAIED:
        case ProjectStep.PROJECT_FILE_COMPLETED:
        case ProjectStep.PROJECT_COMPLETED:
          // case ProjectStep.PROJECT_CLOSED:
          return 'finish'
        case ProjectStep.PROJECT_REFUSED:
        case ProjectStep.PROJECT_FILE_REFUSED:
        case ProjectStep.PROJECT_ABANDONED:
          return 'error'
        default:
          return 'wait'
      }
    }, [data?.data.step])

  const stepsItems = useMemo(
    () =>
      steps.map((step) => {
        if (!step.dateFields) return { title: step.title }
        const dates = step.dateFields
          .map((field) => {
            const value = data?.data[field]
            if (!value) return null
            return `${DATE_LABELS[field]} ${dayjs(value as string).format('DD/MM/YYYY')}`
          })
          .filter(Boolean)
        const description = dates.length > 0 ? dates.join('\n') : undefined
        return { title: step.title, description }
      }),
    [data?.data]
  )

  if (isMobile) {
    return (
      <div style={{ marginTop: '2em', height: '100%' }}>
        {currentItemIdx >= 0 && (
          <>
            <Steps
              direction={'horizontal'}
              status={currentItemStatus}
              current={currentItemIdx}
              items={stepsItems}
              size="small"
            />
            {([AccountType.ARCHITECT, AccountType.COLLABORATOR] as AccountType[]).includes(accountType as AccountType) &&
              !isNull(data?.data?.planning_file_type) && (
                <>
                  <div>{data?.data.planning_file_type}</div>
                  <Divider />
                  <div>{data?.data.reserves}</div>
                  <Col span={18}>
                    <div key={location.pathname}>
                      <Outlet context={{ project: data?.data, invalidateProject }} />
                    </div>
                  </Col>
                </>
              )}
          </>
        )}
      </div>
    )
  } else {
    return (
      <div style={{ marginTop: '2em', height: '100%' }}>
        {showPlanningModal && !isUndefined(data) && (
          <PlanningTypeModal
            project={data.data}
            onSelect={acceptProjectRequest}
            onModalClose={() => {
              setShowPlanningModal(false)
            }}
          />
        )}
        <Modal
          open={showReservesModal}
          centered
          title="Émettre des réserves"
          onCancel={() => setShowReservesModal(false)}
          onOk={updateReserves}
          okText="Enregistrer"
          cancelText="Annuler"
          confirmLoading={isUpdatingReserves}
          okButtonProps={{ disabled: isUpdatingReserves }}
          cancelButtonProps={{ disabled: isUpdatingReserves }}
        >
          <Input.TextArea
            value={reservesText}
            onChange={(e) => setReservesText(e.target.value)}
            rows={4}
            spellCheck
            lang="fr"
          />
        </Modal>

        <Row gutter={[21, 24]} style={{ marginTop: '1.5em' }}>
          <Col span={18}>
            <div key={location.pathname}>
              <Outlet context={{ project: data?.data, invalidateProject }} />
            </div>
          </Col>
          <Col span={6}>
            {currentItemIdx >= 0 && (
              <Steps
                direction="vertical"
                status={currentItemStatus}
                current={currentItemIdx}
                style={{ height: '500px' }}
                items={stepsItems}
              />
            )}
            {showDepositPlatform && (
              <DepositPlatformPanel
                locality={depositLocality}
                departmentCode={depositDepartmentCode}
                commune={depositCommune}
                canEdit={canEditDepositPlatform}
              />
            )}
            {([AccountType.ARCHITECT, AccountType.COLLABORATOR] as AccountType[]).includes(accountType as AccountType) &&
              !isNull(data?.data.planning_file_type) && (
                <Card
                  data-cy={`project-planning-file-type--${kebabCase(
                    data?.data.planning_file_type
                  )}`}
                  title={
                    <CairoPlayTitle
                      title={
                        PlanningFileTypeName[
                          data?.data.planning_file_type as PlanningFileType
                        ]
                      }
                    />
                  }
                  styles={{
                    body: {
                      padding: '12px',
                    },
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignContent: 'center',
                    margin: '1em 0',
                  }}
                >
                  {size(data?.data.reserves) > 0 ? (
                    <div
                      style={{
                        backgroundColor: '#fed7aa',
                        display: 'flex',
                        minHeight: '50px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '6px',
                        borderRadius: '6px',
                      }}
                      data-cy="project-planning-file-type--reserves"
                    >
                      {data?.data.reserves}
                    </div>
                  ) : (
                    <div
                      data-cy="project-planning-file-type--no-reserves"
                      style={{
                        display: 'flex',
                        minHeight: '50px',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      Aucune réserves
                    </div>
                  )}
                  <Divider style={{ margin: '12px 0' }} />
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <ConfigProvider
                      theme={{
                        token: {
                          colorPrimary: '#ea580c',
                        },
                      }}
                    >
                      <Button
                        style={{ width: '100%' }}
                        type="primary"
                        data-cy="action--change-planning-type"
                        onClick={() => setShowPlanningModal(true)}
                      >
                        Changer le type de projet
                      </Button>
                    </ConfigProvider>
                    <ConfigProvider
                      theme={{
                        token: {
                          colorPrimary: '#ea580c',
                        },
                      }}
                    >
                      <Button
                        style={{ width: '100%' }}
                        type="primary"
                        icon={<WarningOutlined />}
                        onClick={openReservesModal}
                      >
                        Émettre des réserves
                      </Button>
                    </ConfigProvider>
                  </Space>
                </Card>
              )}
            {/* The card renders with no salesman too once the office may
                change one (#769): a dossier the architect opened themselves
                carries none, and "who is the commercial" is exactly the
                question this card answers — saying nothing there left the
                office no way in. */}
            {(data?.data.salesman || mayChangeSalesman(accountType)) && (
              <Card
                title={<CairoPlayTitle title="Commercial" />}
                style={{ margin: '1em 0' }}
                data-cy="project-salesman-card"
              >
                {data?.data.salesman ? (
                  <>
                    <p data-cy="project-salesman-name">
                      {data?.data.salesman?.first_name}&nbsp;
                      {data?.data.salesman?.last_name}
                    </p>
                    <p>
                      <b>Email:</b>&nbsp;{data?.data.salesman?.account?.email}
                    </p>
                    <p>
                      <b>Téléphone:</b>&nbsp;{data?.data.salesman?.phone}
                    </p>
                  </>
                ) : (
                  <p data-cy="project-salesman-name">Aucun commercial rattaché.</p>
                )}
                <ChangeSalesmanControl
                  projectId={projectID}
                  currentSalesmanId={data?.data.salesman?.id}
                  onChanged={invalidateProject}
                  block
                />
              </Card>
            )}
            {accountType == AccountType.ARCHITECT &&
              !([
                ProjectStep.PROJECT_COMPLETED,
                ProjectStep.PROJECT_ABANDONED,
              ] as ProjectStep[]).includes(data?.data.step as ProjectStep) && (
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    gap: '12px',
                    margin: '1em 0',
                  }}
                >
                  <ConfigProvider
                    theme={{
                      token: {
                        colorPrimary: '#f97316',
                      },
                    }}
                  >
                    <Popconfirm
                      okButtonProps={{ 'data-cy': 'action-validate-confirm' }}
                      cancelButtonProps={{
                        'data-cy': 'action-validate-cancel',
                      }}
                      icon={null}
                      title="Demande de confirmation"
                      description="Souhaitez vous vraiment fermer le projet ?"
                      trigger="click"
                      onConfirm={abandonProject}
                    >
                      <Button
                        data-cy="action--abandon-project"
                        type="primary"
                        loading={isAbandoning}
                        disabled={isAbandoning}
                      >
                        Fermer le projet
                      </Button>
                    </Popconfirm>
                  </ConfigProvider>
                  <Popconfirm
                    okButtonProps={{ 'data-cy': 'action-validate-confirm' }}
                    cancelButtonProps={{
                      'data-cy': 'action-validate-cancel',
                    }}
                    icon={null}
                    title="Demande de confirmation"
                    description="Souhaitez vous vraiment supprimer le projet ?"
                    trigger="click"
                    onConfirm={disableProject}
                  >
                    <Button
                      data-cy="action--remove-project"
                      type="primary"
                      danger
                      loading={isDisabling}
                      disabled={isDisabling}
                    >
                      Supprimer le projet
                    </Button>
                  </Popconfirm>
                </div>
              )}
          </Col>
        </Row>
      </div>
    )
  }
}

/**
 * Synthesis route layout — shared by both roles. A pure role switcher: the
 * project-hub roles (SALESMAN + SOCIETY) get the salesman chrome, every other
 * role keeps the architect layout. Both provide the same `SynthesisOutletContext`
 * so the dispatcher + numbered step pages render unchanged underneath. Kept as a
 * one-hook switcher (no conditional hooks) so the hook order is stable per role.
 *
 * While the role is still loading we render nothing rather than defaulting to the
 * architect layout: defaulting would briefly mount the architect query (which
 * selects permission-unsafe fields like `salesman.account.email`) for a salesman
 * on a direct synthesis-URL load, risking forbidden GraphQL errors / UI flicker.
 * Mirrors `createRoleSwitcher`'s neutral loading state.
 */
export const ProjectSynthesisLayout: React.FC = () => {
  const { data: accountType, isLoading } = usePermissions()
  if (isLoading || accountType === undefined) return null
  return isProjectHubRole(accountType) ? (
    <SalesmanSynthesisLayout />
  ) : (
    <ArchitectSynthesisLayout />
  )
}
